import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Counter } from '@adornis/counter/counter.js';
import { StudentType } from '@chancenwerk/db/enums/enums.js';
import { StudentParticipationRelationship } from '@chancenwerk/db/semantics/StudentParticipationRelationship.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import {
  subscribeCountPartnerByFilter,
  subscribePartnerByFilter,
} from '@chancenwerk/master-data-management/partner/api/subscribePartnerByFilter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { DateTime } from 'luxon';
import { Partner } from '../entities/Partner.js';

@Entity()
export class KPFStudentInformation extends AdornisEntity {
  public static override _class = 'KPFStudentInformation';

  @Field(type => String)
  public _id: Maybe<string>;

  @Field(type => Int)
  public numberOfOsStudents: Maybe<number>;

  @Field(type => Int)
  public numberOfUsStudents: Maybe<number>;

  @Field(type => Int)
  public maxGrade: Maybe<number>;

  @Field(type => Int)
  public minGrade: Maybe<number>;
}

@Entity()
export class PartnerStudentOverallInformation extends AdornisEntity {
  public static override _class = 'PartnerStudentOverallInformation';

  @Field(type => [KPFStudentInformation], {
    default: v => v ?? [],
  })
  public kpfStudentInformations!: KPFStudentInformation[];
}

const resolveGetActiveCareInformationForPartner = (partnerID: string) => {
  return async (gqlFields: BaseQLSelectionSet<PartnerStudentOverallInformation>) => {
    if (!partnerID) return new PartnerStudentOverallInformation({});

    const kpfColl = await getCollection(KPF);
    const kpfs = await kpfColl.find({ schoolID: partnerID }).toArray();
    const userColl = await getCollection(Student);

    const result = new PartnerStudentOverallInformation({});

    for (const kpf of kpfs) {
      const kpfInformation = new KPFStudentInformation({ _id: kpf._id });

      let studentsOs = await userColl
        .find({
          $and: [
            {
              $or: [
                { 'kuendigung.austrittsdatum': { $exists: false } },
                { 'kuendigung.austrittsdatum': null },
                { 'kuendigung.austrittsdatum': undefined },
                { 'kuendigung.austrittsdatum': { $gt: DateTime.now().toJSDate() } },
              ],
            },
            { schuelertyp: StudentType.OS },
          ],
        })

        .toArray();

      let studentsUs = await userColl
        .find({
          $and: [
            {
              $or: [
                { 'kuendigung.austrittsdatum': { $exists: false } },
                { 'kuendigung.austrittsdatum': null },
                { 'kuendigung.austrittsdatum': undefined },
                { 'kuendigung.austrittsdatum': { $gt: DateTime.now().toJSDate() } },
              ],
            },
            { schuelertyp: StudentType.US },
          ],
        })

        .toArray();

      const relColl = await getRawCollection<StudentParticipationRelationship>(
        StudentParticipationRelationship._collectionName,
      );
      const usersOfKPF = (await relColl.find({ objectID: kpf._id }).toArray()).map(rel => rel.subjectID);

      studentsUs = studentsUs.filter(student => usersOfKPF.includes(student._id));
      studentsOs = studentsOs.filter(student => usersOfKPF.includes(student._id));

      kpfInformation.numberOfOsStudents = studentsOs.length;
      kpfInformation.numberOfUsStudents = studentsUs.length;

      const allStudent = studentsOs.concat(studentsUs);

      let maxGrade = 0;
      let minGrade = 13;
      for (const student of allStudent) {
        if ((student.schoolInformation.jahrgangsstufe ?? 0) > maxGrade) {
          maxGrade = student.schoolInformation.jahrgangsstufe ?? 0;
        }
        if ((student.schoolInformation.jahrgangsstufe ?? 13) < minGrade) {
          minGrade = student.schoolInformation.jahrgangsstufe ?? 0;
        }
      }

      // No Students in the school.
      if (minGrade > maxGrade) {
        minGrade = -1;
        maxGrade = -1;
      }

      kpfInformation.maxGrade = maxGrade;
      kpfInformation.minGrade = minGrade;

      result.kpfStudentInformations.push(kpfInformation);
    }

    return result;
  };
};

const getActiveCareInformationForPartner = registerQuery({
  type: () => PartnerStudentOverallInformation,
  operationName: 'getActiveCareInformationForPartner',
  params: [{ type: () => String, name: 'partnerID' }],
  resolve: resolveGetActiveCareInformationForPartner,
});

export namespace PartnerAPI {
  export const subscribeByFilter = subscribePartnerByFilter;
  export const subscribeCountByFilter = subscribeCountPartnerByFilter;
  export const getStudentStatistics = getActiveCareInformationForPartner;
  export const getNewEvaluationID = registerQuery({
    type: () => String,
    operationName: 'getNewEvaluationID',
    params: [{ name: 'time', type: () => Float }],
    resolve: (time: number) => {
      // ! We need the time to avoid caching of this operation, otherwise the same ID will be returned
      // ! after the first request!
      return async () => {
        return `A-${await Counter.getIncrementingNumber('PARTNER_EVAL_ID')()}`;
      };
    },
  });
  export const doesEvaluationIDExist = registerQuery({
    type: () => Boolean,
    operationName: 'doesEvaluationIDExist',
    params: [
      { type: () => String, name: 'id' },
      { name: 'time', type: () => Float },
    ],
    resolve: (id: string, time: number) => {
      // ! We need the time to avoid caching of this operation, otherwise the same ID will be returned
      // ! after the first request!
      return async () => {
        return (await loadMongoData(Partner, { evaluationID: id })).length > 0;
      };
    },
  });
}
